import React from 'react';
import Select, {
  components,
  OptionProps,
  MenuProps,
  ValueContainerProps,
  GroupBase,
  OptionsOrGroups,
  Props as SelectProps,
} from 'react-select';
import { StylishNewCheckbox } from './StylishNewCheckbox';
import classNames from 'classnames';

export type OptionType = {
  label: string | React.ReactNode;
  value: string | boolean;
};

type MenuComponentProps = MenuProps<OptionType> & {
  value?: OptionType | OptionType[] | null;
  onChange?: (value: OptionType | OptionType[]) => void;
  isMulti: boolean;
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>;
};

const flattenOptions = (
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
): OptionType[] => {
  return options.flatMap((item) => ('options' in item ? item.options : [item]));
};

const Menu = ({
  value,
  onChange,
  isMulti,
  options,
  ...props
}: MenuComponentProps) => {
  const flatOptions = flattenOptions(options);
  const isAllSelected =
    value != null &&
    Array.isArray(value) &&
    value.length === flatOptions.length &&
    flatOptions.every((option: OptionType) =>
      value.some((selected) => selected.value === option.value)
    );

  const handleSelectAll = () => {
    if (isMulti && onChange) {
      if (isAllSelected) {
        onChange([]);
      } else {
        onChange(flatOptions);
      }
    }
  };

  return (
    <components.Menu {...props} isMulti={isMulti} options={options}>
      {isMulti && (
        <div
          role="button"
          onClick={handleSelectAll}
          className="d-flex ms-1 p-2"
        >
          <StylishNewCheckbox
            checked={isAllSelected}
            className="planning-checkbox"
          />
          Select All
        </div>
      )}
      {props.children}
    </components.Menu>
  );
};

const Option = (props: OptionProps<OptionType>) => {
  return (
    <components.Option {...props}>
      <StylishNewCheckbox
        checked={props.isSelected}
        className="planning-checkbox"
      />
      {props.label}
    </components.Option>
  );
};

// Replaces individual MultiValue rendering with a single unified, truncating string box
const CustomValueContainer = ({
  children,
  selectProps,
  ...props
}: ValueContainerProps<OptionType, boolean, GroupBase<OptionType>>) => {
  const selectedOptions = (selectProps.value as OptionType[]) || [];

  // react-select renders [values/placeholder, <Input>] as the container's children. The hidden Input must STAY
  // mounted — and in a stable child position — even when we swap the values for the truncated text: clicking the
  // control opens the menu by focusing that input (dropping it made every select with a selection un-openable),
  // and remounting it on the empty↔selected transition drops focus mid-interaction and closes the menu.
  const childArray = React.Children.toArray(children);
  const input = childArray[childArray.length - 1];
  const valueChildren = childArray.slice(0, -1);

  return (
    <components.ValueContainer selectProps={selectProps} {...props}>
      {selectedOptions.length > 0 ? (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: '4px',
          }}
        >
          {selectedOptions.map((option) => option.label).join(', ')}
        </span>
      ) : (
        valueChildren
      )}
      {input}
    </components.ValueContainer>
  );
};

export type PratusSelectProps = SelectProps<
  OptionType,
  boolean,
  GroupBase<OptionType>
> & {
  label?: string;
  classes?: string;
  style?: React.CSSProperties;
  onChange?: (value: OptionType | OptionType[]) => void;
  value?: OptionType | OptionType[] | null;
  options: OptionsOrGroups<OptionType, GroupBase<OptionType>>;
};

const PratusSelect: React.FC<PratusSelectProps> = ({
  label,
  classes,
  style,
  options,
  defaultValue,
  value,
  onChange,
  isMulti,
  ...props
}) => {
  return (
    <div className={classNames('form-selectbox', classes)} style={style}>
      {label && (
        <label className="mb-2" htmlFor={props.inputId}>
          {label}
        </label>
      )}
      <Select
        inputId={props.inputId}
        className="react-select-container"
        classNamePrefix="react-select"
        options={options}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        closeMenuOnSelect={!isMulti}
        components={{
          ...(isMulti && {
            Option,
          }),
          Menu: (menuProps) => (
            <Menu
              {...menuProps}
              value={value}
              onChange={onChange}
              isMulti={isMulti || false}
              options={options}
            />
          ),
          ValueContainer: CustomValueContainer,
        }}
        hideSelectedOptions={false}
        isMulti={isMulti}
        noOptionsMessage={() => 'No Items Found'}
        isDisabled={props.isDisabled}
        styles={{
          // Custom styles to ensure the value container has a fixed height and truncates overflow text
          valueContainer: (base) => ({
            ...base,
            maxHeight: '38px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexWrap: 'nowrap',
          }),
        }}
        {...props}
      />
    </div>
  );
};

export default PratusSelect;
